import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import FlightDealDuration from 'components/Flights/FlightMerchandising/FlightDealDuration/FlightDealDuration'
import FlightDealAgentHubDisclaimer from 'components/Flights/FlightMerchandising/FlightDealAgentHubDisclaimer/FlightDealAgentHubDisclaimer'
import { isAgentHubEnabled } from 'agentHub/selectors/isAgentHubEnabled'
import DropdownSheet from 'LuxKit/Dropdown/Sheet/DropdownSheet'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import { FLIGHTS_MAX_LEAD_TIME_DAYS, FLIGHTS_MIN_LEAD_TIME_DAYS, FlightsFareTypes } from 'constants/flight'
import FlightSearchWidgetDispatchContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetDispatchContext'
import FlightSearchWidgetStateContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateContext'
import { FlightSearchWidgetActions } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import useFeaturedFlightDeals from 'hooks/Flights/useFeaturedFlightDeals'
import { addDays } from 'lib/datetime/dateUtils'
import type { ComponentProps } from 'react'
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { DROPDOWN_SHEET_MAX_HEIGHT_CSS_VARIABLE } from 'LuxKit/Dropdown/_DropdownSheet'
import FormattedFlightSearchDate from './FlightSearchMenu/FormattedFlightSearchDate'
import FlightsDatePicker from './FlightsDatePicker/FlightsDatePicker'
import FlightsResultsDatePicker from './FlightsDatePicker/FlightsResultsDatePicker'
import FlightsCalendarPriceInfo from './FlightsDatePicker/FlightsCalendarPriceInfo'

const CalendarContent = styled(VerticalSpacer)`
  &.wide-calendar {
    min-inline-size: min(${rem(672)}, calc(100vw - ${rem(80)}));
  }
  &.wide-calendar.results-calendar {
    min-inline-size: min(${rem(748)}, calc(100vw - ${rem(80)}));
  }
`

const CalendarSheetContainer = styled.div`
  &.results-calendar-sheet [data-floating-dropdown] > .size-content.auto-height {
    ${DROPDOWN_SHEET_MAX_HEIGHT_CSS_VARIABLE}: inherit;
  }
`

interface Props
  extends Pick<ComponentProps<typeof DropdownSheet>, 'anchorRef' | 'placement'> {
  id: string
  flightDeals?: Array<App.FlightDeal>
  onDepartingClick?: () => void
  onArrivalClick?: () => void
  onClose?: () => void
  isOpen: boolean
  onDateChange?: (date: Date) => void
  resultsCalendar?: boolean
  hideDayLabels?: boolean
  keepOpenOnSelect?: boolean
  distinguishActiveLeg?: boolean
  mobileResultsRedesign?: boolean
  wideCalendar?: boolean
  portaled?: boolean
  /**
   * How many months the calendar shows. The sheet fills its anchor, so two months only fit where
   * the anchor is as wide as the search bar — a narrow anchor squashes them into a scrolling strip.
   */
  datePickerType?: ComponentProps<typeof FlightsDatePicker>['type']
}

const earliestBookingDate = addDays(new Date(), FLIGHTS_MIN_LEAD_TIME_DAYS)
const finalBookingDate = addDays(new Date(), FLIGHTS_MAX_LEAD_TIME_DAYS)

function FlightSearchDateSelectSheet(props: Props) {
  const { anchorRef, placement, id, onClose, isOpen, onDateChange, flightDeals, hideDayLabels, resultsCalendar, keepOpenOnSelect, distinguishActiveLeg, mobileResultsRedesign, wideCalendar, portaled, datePickerType = 'dual' } = props
  const state = useContext(FlightSearchWidgetStateContext)
  const dispatch = useContext(FlightSearchWidgetDispatchContext)
  const intl = useIntl()

  const currentFlight = useMemo(() => {
    return state.flights.find((flight) => flight.id === id)
  }, [state.flights, id])

  const checkinDate = currentFlight?.checkinDate
  const checkoutDate = currentFlight?.checkoutDate
  const departureAirport = currentFlight?.departureAirport
  const arrivalAirport = currentFlight?.arrivalAirport
  const { fareType, showSegmentActiveDeals, flightDealId } = state

  useEffect(() => {
    if (fareType.value === FlightsFareTypes.ONE_WAY) {
      dispatch({ type: FlightSearchWidgetActions.SET_CHECKOUT_DATE, id, date: null })
    }
  }, [dispatch, fareType.value, id])

  const { featuredRanges, displayDeal } = useFeaturedFlightDeals({
    flightDeals,
    departureAirport,
    arrivalAirport,
    fareType: fareType.value,
    showSegmentActiveDeals,
    flightDealId,
  })

  const showDate = useMemo(() => {
    if (fareType.value === FlightsFareTypes.ONE_WAY) {
      return checkinDate?.isValid()
    }

    return fareType.value === FlightsFareTypes.RETURN
  }, [checkinDate, fareType.value])

  const departingFormField = useRef<HTMLButtonElement | null>(null)
  const initialDatesRef = useRef({ checkinDate, checkoutDate })
  const wasOpenRef = useRef(false)

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      initialDatesRef.current = { checkinDate, checkoutDate }
    }

    wasOpenRef.current = isOpen
  }, [checkinDate, checkoutDate, isOpen])

  const handleCancel = useCallback(() => {
    dispatch({ type: FlightSearchWidgetActions.SET_CHECKIN_DATE, id, date: initialDatesRef.current.checkinDate ?? null })
    dispatch({
      type: FlightSearchWidgetActions.SET_CHECKOUT_DATE,
      id,
      date: fareType.value === FlightsFareTypes.ONE_WAY ? null : initialDatesRef.current.checkoutDate ?? null,
    })
    onClose?.()
  }, [dispatch, fareType.value, id, onClose])

  const applyText = useMemo(
    () =>
      intl.formatMessage({
        id: 'qjqe3TY2rAei5vA37jN_',
        defaultMessage: 'Apply',
        description: 'Button text to apply flight date selection changes',
      }),
    [intl],
  )

  const cancelText = useMemo(
    () =>
      intl.formatMessage({
        id: 'F-YTO1e90A3DqzfDiCrU',
        defaultMessage: 'Cancel',
        description: 'Button text to cancel flight date selection in the flights flow',
      }),
    [intl],
  )

  const priceInfoText = useMemo(
    () =>
      intl.formatMessage({
        id: 'MeAUltVLiiAcd7CwsWte',
        defaultMessage: 'Based on recent return prices (per adult) found',
        description: 'Information text about pricing basis for flight date selection',
      }),
    [intl],
  )

  return (
    <CalendarSheetContainer className={wideCalendar && resultsCalendar ? 'results-calendar-sheet' : undefined}>
      <DropdownSheet
        size={wideCalendar ? 'content' : 'fill-anchor'}
        open={isOpen}
        anchorRef={anchorRef}
        placement={placement}
        portaled={portaled}
        autoHeight={portaled || (wideCalendar && resultsCalendar)}
        triggerRef={departingFormField}
        onClose={onClose}
        primaryActionProps={{
          'data-testid': 'apply-flight-search-changes',
          type: 'submit',
          children: applyText,
          onClick: onClose,
        }}
        secondaryActionProps={{
          kind: 'tertiary',
          children: cancelText,
          onClick: handleCancel,
        }}
        footerStart={
          <Group
            direction="horizontal"
            horizontalAlign="space-between"
            verticalAlign="center"
          >
            {resultsCalendar || mobileResultsRedesign ? <FlightsCalendarPriceInfo /> : <BodyText variant="small" colour="light-foreground-alt">
              {priceInfoText}
            </BodyText>}

            {showDate && !resultsCalendar && !mobileResultsRedesign && (
              <FormattedFlightSearchDate
                checkInDate={checkinDate}
                checkOutDate={checkoutDate}
                fareType={fareType.value}
              />
            )}
          </Group>
        }
      >
        <CalendarContent
          gap={24}
          key={`${id}-${isOpen}`}
          className={wideCalendar ? (resultsCalendar ? 'wide-calendar results-calendar' : 'wide-calendar') : undefined}
        >
          {displayDeal && !isAgentHubEnabled && <FlightDealDuration flightDeal={displayDeal} />}
          {isAgentHubEnabled && <FlightDealAgentHubDisclaimer />}

          {mobileResultsRedesign ? <FlightsResultsDatePicker
            minDate={earliestBookingDate}
            maxDate={finalBookingDate}
            id={id}
            featuredRanges={featuredRanges}
          /> : <FlightsDatePicker
            onDateChange={onDateChange}
            minDate={earliestBookingDate}
            maxDate={finalBookingDate}
            type={datePickerType}
            id={id}
            featuredRanges={featuredRanges}
            hideDayLabels={hideDayLabels}
            resultsCalendar={resultsCalendar}
            keepOpenOnSelect={keepOpenOnSelect}
            distinguishActiveLeg={distinguishActiveLeg}
          />}
        </CalendarContent>
      </DropdownSheet>
    </CalendarSheetContainer>
  )
}

export default FlightSearchDateSelectSheet
